import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Trash2 } from "lucide-react";
import { FormField } from "@/components/ui/FormField";
import { SelectField } from "@/components/ui/SelectField";
import { FieldGroup } from "@/components/biens/FieldGroup";
import { getOrientations } from "@/data/options";
import type { SurfaceOrientation } from "@/types/bien";
import { cn } from "@/lib/utils";

interface SurfaceOrientationRepeaterProps {
  /** Détermine les libellés (chambre / terrasse). */
  kind: "chambre" | "terrasse";
  rows: SurfaceOrientation[];
  onChange: (next: SurfaceOrientation[]) => void;
  /** false → rendu figé (droit de lecture seule sur le champ). */
  editable: boolean;
  /**
   * Nombre de lignes PILOTÉ par un autre champ (`nbChambres`).
   *
   * Quand il est fourni, les lignes apparaissent d'elles-mêmes et les boutons
   * « Ajouter » / « Supprimer » disparaissent : le nombre de chambres est déjà
   * une donnée de la fiche, le redemander à l'utilisateur était une saisie en
   * double qui pouvait de surcroît contredire le champ.
   *
   * Les lignes déjà saisies au-delà du compte restent affichées — on ne cache
   * jamais une valeur existante ; elles disparaissent quand l'utilisateur
   * corrige le compte.
   */
  count?: number;
  className?: string;
}

/**
 * Répéteur « surface + orientation », partagé fiche vente / fiche location.
 *
 * Décision spec : « Chambres et terrasses — répéteur unique surface +
 * orientation, sur les deux fiches ». La logique de patch (miroirs
 * `surfacesChambres` / `surfaceTerrasse`, synchro `nbChambres`) vit dans
 * `@/lib/surfaceOrientationRows`.
 *
 * MISE EN PAGE (2026-08-08) : chaque entrée est une cellule compacte de la
 * grille — libellé « Chambre N » puis surface et orientation côte à côte —
 * et trois entrées tiennent sur une ligne. Une surface fait deux caractères,
 * elle n'a jamais eu besoin d'une demi-largeur d'écran.
 */
export function SurfaceOrientationRepeater({
  kind,
  rows,
  onChange,
  editable,
  count,
  className,
}: SurfaceOrientationRepeaterProps) {
  const { t } = useTranslation();
  const orientations = useMemo(() => getOrientations(t), [t]);
  const isTerrasse = kind === "terrasse";
  const driven = typeof count === "number";

  // Piloté par un compte : on affiche `count` lignes, sans jamais masquer une
  // ligne déjà saisie au-delà.
  const shown = driven ? Math.max(count ?? 0, rows.length) : rows.length;
  const cells = Array.from({ length: shown }, (_, i) => rows[i] ?? { surface: "", orientation: "" });

  const patch = (i: number, part: Partial<SurfaceOrientation>) => {
    const next = Array.from({ length: Math.max(shown, rows.length) }, (_, j) => rows[j] ?? { surface: "", orientation: "" });
    next[i] = { ...next[i], ...part };
    onChange(next);
  };

  const addButton = (
    <button
      type="button"
      onClick={() => onChange([...rows, { surface: "", orientation: "" }])}
      className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-text-secondary border border-dashed border-border rounded-md hover:border-accent hover:text-accent bg-surface transition-colors cursor-pointer outline-none shadow-none"
    >
      <Plus size={15} />
      {t(isTerrasse ? "biens.repeater.addTerrasse" : "biens.repeater.addChambre")}
    </button>
  );

  if (shown === 0) {
    // En mode LIBRE, l'état vide doit garder son bouton d'ajout : sans lui, un
    // bien sans terrasse ne pouvait plus en recevoir une (l'ancien répéteur de
    // la fiche vente affichait le bouton en permanence).
    return (
      <div className={cn("flex flex-col items-start gap-3", className)}>
        <p className="text-xs text-text-muted">
          {t(driven ? `biens.repeater.${kind}CountHint` : `biens.repeater.${kind}Empty`)}
        </p>
        {!driven && editable && addButton}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "grid grid-cols-12 gap-x-4 gap-y-4",
        !editable && "pointer-events-none select-none opacity-80",
        className,
      )}
    >
      {cells.map((row, i) => (
        <div key={`${kind}-${i}`} className="col-span-12 sm:col-span-6 md:col-span-4">
          <FieldGroup
            legend={t(`biens.repeater.${kind}N`, { index: i + 1 })}
            units={[
              {
                key: "surface",
                weight: 1,
                node: (
                  <FormField
                    label={t("biens.repeater.unitSurface")}
                    value={row.surface}
                    onChange={(v) => patch(i, { surface: v })}
                    align="center"
                    labelBelow
                  />
                ),
              },
              {
                key: "orientation",
                weight: 2,
                node: (
                  <SelectField
                    label={t("biens.repeater.unitOrientation")}
                    value={row.orientation}
                    onChange={(v) => patch(i, { orientation: v })}
                    options={orientations}
                    align="center"
                    labelBelow
                  />
                ),
              },
              // Suppression : en mode libre, et — en mode piloté — sur les
              // seules lignes EXCÉDENTAIRES. Sans cette échappatoire, une fiche
              // dont `chambresDetail` porte plus d'entrées que `nbChambres`
              // (donnée héritée, import, saisie antérieure) affiche des lignes
              // fantômes que rien ne permet plus d'enlever.
              ...((driven ? i >= (count ?? 0) : true) && editable
                ? [
                    {
                      key: "del",
                      weight: 0.45,
                      node: (
                        <button
                          type="button"
                          onClick={() => onChange(rows.filter((_, j) => j !== i))}
                          className="h-[38px] flex items-center justify-center rounded-md text-text-muted hover:text-danger hover:bg-danger/10 transition-colors cursor-pointer outline-none shadow-none"
                          aria-label={t("common.delete")}
                          title={t("common.delete")}
                        >
                          <Trash2 size={15} />
                        </button>
                      ),
                    },
                  ]
                : []),
            ]}
          />
        </div>
      ))}

      {/* Ajout manuel : réservé au mode libre. Les terrasses n'ont pas de champ
          « nombre de terrasses » qui pourrait piloter les lignes. */}
      {!driven && editable && <div className="col-span-12">{addButton}</div>}
    </div>
  );
}
